/**
 * CANONICAL KEYBOARD CONTROLLER (single source of truth)
 * ─────────────────────────────────────────────────────────────
 * One listener for the whole app. Writes the live keyboard height
 * to `--kb` (CSS custom property on <html>). Every composer,
 * scroll container, and tab bar in Kinly reads from this single
 * variable — no per-screen logic, no prop drilling, no React state.
 *
 *   --kb        live keyboard height in px (0 when hidden)
 *
 * Capacitor mode is FORCED to `None` — we own the layout. With
 * Body / Native modes the WebView resizes underneath us and fights
 * any fixed-position element, producing the mid-screen-floating
 * input bug we kept chasing. Keeping resize off and lifting fixed
 * elements via `bottom: var(--kb)` is the only path that behaves
 * identically on iOS, Android, and the web preview.
 */
import { Capacitor } from "@capacitor/core";

const HTML = () => document.documentElement;
const setKb = (h) => HTML().style.setProperty("--kb", `${Math.max(0, Math.round(h))}px`);
const setKbState = (open) => {
  HTML().dataset.kb = open ? "open" : "closed";
};

export async function initKeyboardBehavior() {
  // Initialise to closed.
  setKb(0);
  setKbState(false);

  // GLOBAL focus-scroll-into-view. Applies to EVERY <input>, <textarea>,
  // and [contenteditable] in the app — auth forms, event-create sheets,
  // poll editor, settings inputs. Whenever an input gets focused and the
  // keyboard is open (or about to be), we scroll the element so its
  // bottom edge sits comfortably above the keyboard.
  //
  // Why this is canonical:
  //   - Capacitor `KeyboardResize.None` doesn't move the viewport — we
  //     have to do the scroll ourselves.
  //   - `scrollIntoView({block:'center'})` works for inputs inside any
  //     scroll container (including bottom-sheets, modals, the AppShell
  //     scroll area). The browser walks up to find each scrollable
  //     ancestor and scrolls all of them so the target is visible.
  //   - Runs on every focus regardless of native/web — same behaviour
  //     across iOS, Android, and dev preview.
  const isTypeable = (el) => {
    if (!el) return false;
    if (el.tagName === "TEXTAREA") return true;
    if (el.isContentEditable) return true;
    if (el.tagName === "INPUT") {
      const t = (el.getAttribute("type") || "text").toLowerCase();
      return !["button", "submit", "checkbox", "radio", "file", "range", "color", "hidden"].includes(t);
    }
    return false;
  };
  const focusHandler = (e) => {
    const el = e.target;
    if (!isTypeable(el)) return;
    // Two attempts: once immediately (for synchronous keyboard show on
    // touch), and once ~280ms later when the native keyboard has
    // finished animating and `--kb` reflects its true height.
    const doScroll = () => {
      try { el.scrollIntoView({ block: "center", behavior: "smooth" }); } catch { /* noop */ }
    };
    doScroll();
    setTimeout(doScroll, 320);
  };
  document.addEventListener("focusin", focusHandler, true);

  if (Capacitor.isNativePlatform()) {
    await initNative();
  } else {
    initWeb();
  }
}

async function initNative() {
  try {
    const { Keyboard, KeyboardResize } = await import("@capacitor/keyboard");
    // Keep the WebView frame stable. We do NOT rely on Capacitor's
    // `keyboardHeight` (it's stale, doesn't include accessory bars, and
    // doesn't update on live keyboard switches). Instead we read the
    // live visualViewport — see initVisualViewport() below.
    await Keyboard.setResizeMode({ mode: KeyboardResize.None });
    try { await Keyboard.setAccessoryBarVisible({ isVisible: false }); } catch { /* android */ }
    // setScroll({isDisabled:true}) intentionally removed — it was preventing
    // the WebView from accepting the focus-scroll-into-view calls we issue
    // ourselves above. Without setScroll, iOS/Android handle keyboard scroll
    // natively and our focusHandler nudges the focused input into view.
    // Use Capacitor events ONLY as state hints — open/closed flag for the
    // tab bar hide-on-keyboard CSS rule. The actual height comes from
    // visualViewport which is always accurate.
    Keyboard.addListener("keyboardWillShow", (info) => {
      setKbState(true);
      // Fallback: visualViewport sometimes hasn't reported the obstruction
      // yet when this fires (especially with 3rd-party keyboards like
      // SwiftKey). If --kb is still < 50px, seed it from Capacitor's
      // reported height so the composer doesn't render under an
      // invisible obstruction. visualViewport will overwrite this
      // moments later with the precise value (including accessory bar).
      const current = parseInt(HTML().style.getPropertyValue("--kb") || "0", 10);
      if (info?.keyboardHeight && current < 50) {
        setKb(info.keyboardHeight);
      }
    });
    Keyboard.addListener("keyboardDidHide", () => setKbState(false));
  } catch (e) {
    console.warn("[Kinly] keyboard init skipped:", e?.message || e);
  }
  // visualViewport is available on iOS WKWebView and Android WebView and
  // updates LIVE on any obstruction change (keyboard show/hide, accessory
  // bar appearance, switching between SwiftKey / iOS / Gboard mid-typing,
  // emoji bar toggle, predictive-text on/off). This is the only API that
  // tracks ALL of those correctly.
  initVisualViewport();
}

function initWeb() {
  initVisualViewport();
}

function initVisualViewport() {
  const vv = window.visualViewport;
  if (!vv) return;
  const onResize = () => {
    // ⚠️ DO NOT change this threshold or the formula without re-testing on
    //    iOS TestFlight. History (2026-06-18): a too-low threshold (80px)
    //    combined with subtracting `vv.offsetTop` caused FALSE POSITIVE
    //    "keyboard is open" events on every scroll on Plan / Activities /
    //    Whereabouts — those pages have long scrollable content, and
    //    iOS WKWebView's rubber-band scroll briefly drives `vv.offsetTop`
    //    negative, which artificially inflated the computed obstruction
    //    above 80px. Result: `[data-kb="open"]` set → `.hide-when-kb`
    //    rule hid the TabBar (display: none) → user appeared stuck on
    //    the screen because the bottom nav was no longer in the DOM.
    //
    //    Real iPhone keyboards are at minimum ~216px tall (smallest =
    //    iPhone SE landscape). 150px is a safe threshold: well below any
    //    real keyboard, far above any scroll/status-bar artefact.
    //
    //    The classic obstruction formula is just `innerHeight - vv.height`
    //    — we removed the offsetTop subtraction because it was the
    //    direct source of the false positives.
    const obstruction = Math.max(0, window.innerHeight - vv.height);
    setKb(obstruction);
    setKbState(obstruction > 150);
  };
  vv.addEventListener("resize", onResize);
  vv.addEventListener("scroll", onResize);
  onResize();
}
